import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap, useNodesState, useEdgesState, Handle, Position, addEdge,
} from "reactflow";
import "reactflow/dist/style.css";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, X, House, ChatCircle, Trash, FloppyDisk, ArrowLeft, Robot, Buildings, SignOut, Warning,
  PaperPlaneTilt, ArrowClockwise, Play,
} from "@phosphor-icons/react";

const ACTIONS = [
  { v: "none", label: "Nada (continuar bot)" },
  { v: "go_to_menu", label: "Ir para outro menu" },
  { v: "transfer_department", label: "Transferir para departamento" },
  { v: "ai_agent", label: "Agente de IA" },
  { v: "end_chat", label: "Encerrar atendimento" },
  { v: "external_request", label: "Fazer requisição externa" },
];

const MSG_TYPES = [
  { v: "text", label: "Texto" },
  { v: "image", label: "Imagem" },
  { v: "audio", label: "Áudio" },
  { v: "document", label: "Documento" },
  { v: "location", label: "Localização" },
];

const VALIDATIONS = [
  { v: "none", label: "Nenhum (sem validação)" },
  { v: "plain-text", label: "Texto simples" },
  { v: "number", label: "Número" },
  { v: "email", label: "E-mail" },
  { v: "phone", label: "Telefone" },
  { v: "cpf", label: "CPF" },
  { v: "cnpj", label: "CNPJ" },
  { v: "cep", label: "CEP" },
  { v: "date", label: "Data (dd/mm/aaaa)" },
  { v: "time", label: "Horário (hh:mm)" },
];

const WA_OPTION_LIMIT = 20;

// -----------------------------------------------------------------------------
function FlowNode({ data, selected }) {
  return (
    <div
      className={`bg-white border-2 shadow-brutal p-3 min-w-[240px] ${selected ? "border-[#0F4C3A]" : "border-[#E5E5E0]"}`}
      onDoubleClick={() => data.onEdit?.()}
      data-testid={`flow-node-${data.id}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-[#0F4C3A] !w-3 !h-3" />
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#E5E5E0]">
        <div className="flex items-center gap-1.5">
          {data.is_root ? <House size={14} weight="fill" className="text-[#0F4C3A]" /> : <ChatCircle size={14} className="text-[#5C5C59]" />}
          <span className="font-black text-sm truncate max-w-[150px]">{data.identifier || "Sem nome"}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); data.onEdit?.(); }} className="text-[#0F4C3A] text-[10px] font-bold hover:underline">
          Editar ›
        </button>
      </div>
      <div className="text-[9px] uppercase tracking-wider text-[#5C5C59]">Mensagem</div>
      <div className="text-xs text-[#1A1A1A] truncate max-w-[220px] mb-2">{data.message?.text || "(vazio)"}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#5C5C59]">Formato</div>
      <div className="text-xs mb-1">
        {data.format === "options" ? `Opções (${data.options?.length || 0})` : data.format === "text" ? `Texto — ${data.validation}` : "Requisição externa"}
      </div>
      {data.format === "options" && (data.options || []).map((op, i) => (
        <div key={op.id || `op-${i}`} className="text-[11px] px-2 py-1 bg-[#EDF3F0] mt-1 flex items-center justify-between relative">
          <span className="truncate flex-1">{op.text || `Opção ${i + 1}`}</span>
          {op.action === "transfer_department" && <Buildings size={10} className="text-[#D97706] ml-1" />}
          {op.action === "ai_agent" && <Robot size={10} className="text-[#D97706] ml-1" />}
          {op.action === "end_chat" && <SignOut size={10} className="text-[#DC2626] ml-1" />}
          <Handle
            type="source"
            position={Position.Right}
            id={`opt-${i}`}
            className="!bg-[#0F4C3A] !w-3 !h-3 !right-[-10px]"
          />
        </div>
      ))}
      {/* Next-step handle. For text/external → this is the ONLY exit (solid green).
          For options → this is the fallback path (dashed orange). */}
      {data.format !== "options" ? (
        <div className="mt-2 pt-2 border-t border-[#0F4C3A] flex items-center justify-between text-[9px] uppercase tracking-wider text-[#0F4C3A] font-bold relative">
          <span>Continuar para →</span>
          <Handle
            type="source"
            position={Position.Right}
            id="fallback"
            className="!bg-[#0F4C3A] !w-3 !h-3 !right-[-10px]"
          />
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-dashed border-[#D97706] flex items-center justify-between text-[9px] uppercase tracking-wider text-[#D97706] font-bold relative">
          <span>Se nada casar →</span>
          <Handle
            type="source"
            position={Position.Bottom}
            id="fallback"
            className="!bg-[#D97706] !w-3 !h-3 !bottom-[-10px]"
          />
        </div>
      )}
    </div>
  );
}

function TerminalNode({ data }) {
  const styleMap = {
    transfer_department: { bg: "#FEE2E2", border: "#DC2626", icon: Buildings, label: "Transferência para departamento" },
    ai_agent: { bg: "#FEF3C7", border: "#D97706", icon: Robot, label: "Agente de IA" },
    end_chat: { bg: "#F7F7F5", border: "#5C5C59", icon: SignOut, label: "Encerrar atendimento" },
  };
  const s = styleMap[data.kind] || styleMap.end_chat;
  const Icon = s.icon;
  return (
    <div
      className="border-2 shadow-brutal p-2 text-[10px] min-w-[180px]"
      style={{ background: s.bg, borderColor: s.border, color: s.border }}
      data-testid={`terminal-node-${data.kind}`}
    >
      <Handle type="target" position={Position.Left} className="!w-3 !h-3" style={{ background: s.border }} />
      <div className="flex items-center gap-1 font-bold">
        <Icon size={12} weight="fill" /> {s.label}
      </div>
      {!data.configured && (
        <div className="mt-1 flex items-center gap-1 text-[9px]">
          <Warning size={9} weight="fill" /> Não selecionado
        </div>
      )}
      {data.configured && data.detail && (
        <div className="mt-1 text-[9px] opacity-70">{data.detail}</div>
      )}
    </div>
  );
}

const nodeTypes = { flowNode: FlowNode, terminal: TerminalNode };

// -----------------------------------------------------------------------------
export default function ChatbotDiagram() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [flows, setFlows] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [simOpen, setSimOpen] = useState(false);
  const [botEnabled, setBotEnabled] = useState(null); // null=loading
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, s, cfg] = await Promise.all([
        api.get("/chatbot/flows"),
        api.get("/company/sectors").catch(() => ({ data: [] })),
        api.get("/chatbot/settings").catch(() => ({ data: null })),
      ]);
      setFlows(f.data);
      setSectors(s.data || []);
      setBotEnabled(cfg.data ? !!cfg.data.enabled : false);
      const flowId = params.get("flow");
      if (flowId) {
        const f2 = f.data.find((x) => x.id === flowId);
        if (f2) setEditing(f2);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const toggleBot = async () => {
    try {
      const { data } = await api.put("/chatbot/settings", { enabled: !botEnabled });
      setBotEnabled(!!data.enabled);
      toast.success(data.enabled ? "Bot ativado no WhatsApp real" : "Bot desativado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Build nodes + edges (menus + terminal nodes)
  useEffect(() => {
    const N = flows.map((f) => ({
      id: f.id,
      type: "flowNode",
      position: f.position || { x: 100, y: 100 },
      data: { ...f, onEdit: () => setEditing(f) },
    }));
    const E = [];
    // Terminal nodes: computed as virtual nodes so user sees "Não selecionado" etc.
    flows.forEach((f) => {
      const base = f.position || { x: 100, y: 100 };
      let terminalOffset = 0;
      (f.options || []).forEach((op, idx) => {
        if (op.action === "go_to_menu" && op.action_config?.next_flow_id) {
          E.push({
            id: `${f.id}-${idx}-go`,
            source: f.id, sourceHandle: `opt-${idx}`,
            target: op.action_config.next_flow_id,
            style: { stroke: "#0F4C3A", strokeWidth: 2 },
            animated: true,
          });
        } else if (["transfer_department", "ai_agent", "end_chat"].includes(op.action)) {
          const tid = `${f.id}-t-${idx}`;
          const cfg = op.action_config || {};
          const sector = sectors.find((s) => s.id === cfg.sector_id);
          const configured = op.action === "transfer_department" ? !!sector : op.action === "ai_agent" ? true : true;
          const detail = op.action === "transfer_department" ? (sector?.name || "") : op.action === "ai_agent" ? "Carregando…" : "Fim";
          N.push({
            id: tid,
            type: "terminal",
            position: { x: base.x + 320, y: base.y + 60 + terminalOffset },
            data: { kind: op.action, configured, detail },
            draggable: true,
          });
          E.push({
            id: `${f.id}-${idx}-term`,
            source: f.id, sourceHandle: `opt-${idx}`,
            target: tid,
            style: { stroke: op.action === "end_chat" ? "#5C5C59" : op.action === "ai_agent" ? "#D97706" : "#DC2626", strokeWidth: 2 },
          });
          terminalOffset += 60;
        }
      });

      // action_after (fallback) — creates edge/terminal from bottom handle
      const aa = f.action_after || {};
      const isPrimaryPath = f.format !== "options"; // texto/requisição: fallback é o caminho principal
      const edgeColor = isPrimaryPath ? "#0F4C3A" : "#D97706";
      const edgeDash = isPrimaryPath ? undefined : "5 5";
      const edgeLabel = isPrimaryPath ? "próximo" : "se nada casar";
      if (aa.type === "go_to_menu" && aa.next_flow_id) {
        E.push({
          id: `${f.id}-fallback-go`,
          source: f.id, sourceHandle: "fallback",
          target: aa.next_flow_id,
          style: { stroke: edgeColor, strokeWidth: 2, strokeDasharray: edgeDash },
          animated: true,
          label: edgeLabel,
          labelStyle: { fontSize: 9, fill: edgeColor },
        });
      } else if (["transfer_department", "ai_agent", "end_chat"].includes(aa.type)) {
        const tid = `${f.id}-t-fallback`;
        const sector = sectors.find((s) => s.id === aa.sector_id);
        const configured = aa.type === "transfer_department" ? !!sector : true;
        const detail = aa.type === "transfer_department" ? (sector?.name || "") : aa.type === "ai_agent" ? "IA" : "Fim";
        N.push({
          id: tid,
          type: "terminal",
          position: { x: base.x + 40, y: base.y + 260 + terminalOffset },
          data: { kind: aa.type, configured, detail },
          draggable: true,
        });
        E.push({
          id: `${f.id}-fallback-term`,
          source: f.id, sourceHandle: "fallback",
          target: tid,
          style: { stroke: aa.type === "end_chat" ? "#5C5C59" : aa.type === "ai_agent" ? "#D97706" : "#DC2626", strokeWidth: 2, strokeDasharray: edgeDash },
        });
      }
    });
    setNodes(N);
    setEdges(E);
  }, [flows, sectors, setNodes, setEdges]);

  // Drag-and-drop connection — dragging from an option handle to another menu
  // auto-configures that option's action = go_to_menu
  // Dragging from the fallback handle auto-configures action_after = go_to_menu
  const onConnect = useCallback(async (params) => {
    const { source, sourceHandle, target } = params;
    if (source === target) return;
    const targetIsFlow = flows.some((x) => x.id === target);
    if (!targetIsFlow) {
      toast.error("Destino deve ser outro menu");
      return;
    }
    const src = flows.find((x) => x.id === source);
    if (!src) return;

    // Fallback handle (bottom, orange) — sets action_after
    if (sourceHandle === "fallback" || sourceHandle === "text-out") {
      try {
        const { data } = await api.put(`/chatbot/flows/${src.id}`, {
          ...src,
          action_after: { type: "go_to_menu", next_flow_id: target },
        });
        setFlows((prev) => prev.map((f) => (f.id === data.id ? data : f)));
        toast.success("Ação após menu configurada");
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }

    if (!sourceHandle || !sourceHandle.startsWith("opt-")) {
      toast.error("Conecte a partir de uma opção ou do handle laranja");
      return;
    }
    const idx = parseInt(sourceHandle.replace("opt-", ""), 10);
    const newOpts = [...(src.options || [])];
    newOpts[idx] = {
      ...(newOpts[idx] || { text: `Opção ${idx + 1}` }),
      action: "go_to_menu",
      action_config: { next_flow_id: target },
    };
    try {
      const { data } = await api.put(`/chatbot/flows/${src.id}`, { ...src, options: newOpts });
      setFlows((prev) => prev.map((f) => (f.id === data.id ? data : f)));
      toast.success("Menus conectados");
    } catch (e) { toast.error(formatApiError(e)); }
  }, [flows]);

  const addFlow = async () => {
    const identifier = window.prompt("Nome do novo menu:");
    if (!identifier) return;
    try {
      const { data } = await api.post("/chatbot/flows", {
        identifier: identifier.trim(),
        is_root: flows.length === 0,
        position: { x: 100 + Math.random() * 300, y: 100 + Math.random() * 200 },
        message: { type: "text", text: "" },
        format: "options",
        options: [],
      });
      setFlows((prev) => [...prev, data]);
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveFlow = async (updated) => {
    try {
      const { data } = await api.put(`/chatbot/flows/${updated.id}`, updated);
      setFlows((prev) => prev.map((f) => (f.id === data.id ? data : f)));
      toast.success("Menu salvo");
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteFlow = async (id) => {
    if (!window.confirm("Excluir este menu?")) return;
    try {
      await api.delete(`/chatbot/flows/${id}`);
      setFlows((prev) => prev.filter((f) => f.id !== id));
      setEditing(null);
      toast.success("Menu excluído");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onNodeDragStop = async (_e, node) => {
    if (node.type !== "flowNode") return; // don't persist virtual terminal positions
    const f = flows.find((x) => x.id === node.id);
    if (!f) return;
    try {
      await api.put(`/chatbot/flows/${f.id}`, { ...f, position: node.position });
      setFlows((prev) => prev.map((x) => (x.id === f.id ? { ...x, position: node.position } : x)));
    } catch { /* ignore */ }
  };

  return (
    <div className="h-screen flex flex-col" data-testid="diagram-page">
      <div className="p-3 border-b border-[#E5E5E0] flex items-center gap-2 bg-white">
        <Button variant="ghost" onClick={() => navigate("/chatbot")} className="rounded-none">
          <ArrowLeft size={16} weight="bold" className="mr-1" /> Voltar
        </Button>
        <div className="ml-2 font-black">Diagrama do chatbot</div>
        <span className="text-xs text-[#5C5C59] ml-3 hidden md:inline">
          🟢 Verde = opção escolhida · 🟠 Laranja = &quot;se nada casar&quot;
        </span>
        <div className="flex-1" />
        {botEnabled === false && (
          <button
            onClick={toggleBot}
            data-testid="quick-enable-bot"
            className="mr-2 px-3 py-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold border-2 border-[#DC2626] flex items-center gap-1"
            title="O bot NÃO responde no WhatsApp real enquanto estiver desativado"
          >
            <Warning size={12} weight="fill" /> Bot desligado — clique pra ativar
          </button>
        )}
        {botEnabled === true && (
          <span className="mr-2 px-2 py-1 bg-[#DCF8C6] text-[#0F4C3A] text-[10px] font-bold border border-[#0F4C3A] flex items-center gap-1" title="Bot ativo no WhatsApp real">
            🟢 Bot no ar
          </span>
        )}
        <Button onClick={() => setSimOpen((v) => !v)} data-testid="simulator-toggle-btn" className="rounded-none bg-white border-2 border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#EDF3F0] mr-2">
          <Play size={14} weight="bold" className="mr-1" /> {simOpen ? "Fechar simulador" : "Testar aqui"}
        </Button>
        <Button onClick={() => setTemplatesOpen(true)} data-testid="templates-btn" className="rounded-none bg-white border-2 border-[#D97706] text-[#D97706] hover:bg-[#FEF3E7] mr-2">
          ✨ Templates prontos
        </Button>
        <Button onClick={addFlow} data-testid="add-menu-btn" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">
          <Plus size={14} weight="bold" className="mr-1" /> Adicionar menu
        </Button>
      </div>

      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          onNodeDragStop={onNodeDragStop}
          fitView
          defaultEdgeOptions={{ style: { strokeWidth: 2 } }}
        >
          <Background gap={16} color="#E5E5E0" />
          <Controls />
          <MiniMap nodeStrokeColor="#0F4C3A" nodeColor="#EDF3F0" pannable />
        </ReactFlow>

        {flows.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-[#5C5C59]">
              <p className="font-semibold">Nenhum menu ainda.</p>
              <p className="text-xs mt-1">Clique em &quot;Adicionar menu&quot; pra criar o primeiro passo do bot.</p>
            </div>
          </div>
        )}

        {editing && (
          <NodeEditor
            flow={editing}
            flows={flows}
            sectors={sectors}
            onClose={() => setEditing(null)}
            onSave={saveFlow}
            onDelete={deleteFlow}
          />
        )}

        {simOpen && <SimulatorPanel onClose={() => setSimOpen(false)} />}

        {templatesOpen && (
          <TemplatesModal
            onClose={() => setTemplatesOpen(false)}
            onApplied={() => { setTemplatesOpen(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function NodeEditor({ flow, flows, sectors, onClose, onSave, onDelete }) {
  const [f, setF] = useState({
    ...flow,
    message: flow.message || { type: "text", text: "" },
    messages: flow.messages || [],
    options: flow.options || [],
    external_request: flow.external_request || { method: "GET", url: "", headers: {}, body: "", result_variable: "" },
    validation: flow.validation || "none",
    prefer_buttons_or_lists: !!flow.prefer_buttons_or_lists,
    invalid_input_message: flow.invalid_input_message || "",
  });

  const setField = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setMsg = (k, v) => setF((prev) => ({ ...prev, message: { ...prev.message, [k]: v } }));
  const setExt = (k, v) => setF((prev) => ({ ...prev, external_request: { ...prev.external_request, [k]: v } }));

  const setExtraMsg = (i, patch) => {
    const arr = [...f.messages];
    arr[i] = { ...arr[i], ...patch };
    setField("messages", arr);
  };
  const addExtraMsg = () => setField("messages", [...f.messages, { id: `msg-${Date.now()}`, type: "text", text: "" }]);
  const removeExtraMsg = (i) => setField("messages", f.messages.filter((_, idx) => idx !== i));

  const setOption = (idx, patch) => {
    const opts = [...f.options];
    opts[idx] = { ...opts[idx], ...patch };
    setField("options", opts);
  };
  const addOption = () => setField("options", [...f.options, { text: "", action: "none", action_config: {} }]);
  const removeOption = (idx) => setField("options", f.options.filter((_, i) => i !== idx));
  const moveOption = (idx, delta) => {
    const opts = [...f.options];
    const j = idx + delta;
    if (j < 0 || j >= opts.length) return;
    [opts[idx], opts[j]] = [opts[j], opts[idx]];
    setField("options", opts);
  };

  const totalOverLimit = f.prefer_buttons_or_lists
    ? f.options.filter((o) => (o.text || "").length > WA_OPTION_LIMIT).length
    : 0;

  return (
    <div className="absolute top-0 right-0 h-full w-[440px] bg-white border-l-2 border-[#0F4C3A] shadow-brutal overflow-y-auto z-10" data-testid="node-editor">
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5E0] px-4 py-3 flex items-center justify-between">
        <div className="font-black">Editar Menu</div>
        <div className="flex items-center gap-1">
          <Button onClick={() => onSave(f)} size="sm" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white" data-testid="save-flow-btn">
            <FloppyDisk size={14} weight="bold" className="mr-1" /> Salvar
          </Button>
          <button onClick={() => onDelete(f.id)} className="p-2 border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" title="Excluir" data-testid="delete-flow-btn">
            <Trash size={12} weight="bold" />
          </button>
          <button onClick={onClose} className="p-2 hover:bg-[#F7F7F5]" title="Fechar"><X size={16} weight="bold" /></button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <Label className="text-xs uppercase font-semibold tracking-wider">Identificador</Label>
          <p className="text-[10px] text-[#5C5C59]">Título interno. Não é enviado ao cliente.</p>
          <Input value={f.identifier} onChange={(e) => setField("identifier", e.target.value)} className="rounded-none mt-1" data-testid="edit-identifier" />
          <label className="flex items-center gap-2 mt-2">
            <Switch checked={!!f.is_root} onCheckedChange={(v) => setField("is_root", v)} data-testid="edit-is-root" />
            <span className="text-xs">Este é o <b>menu inicial</b> (primeira mensagem do bot)</span>
          </label>
        </div>

        {/* Mensagens */}
        <fieldset className="border border-[#E5E5E0] p-3">
          <legend className="text-xs uppercase font-semibold px-1">Mensagem do menu</legend>
          <p className="text-[10px] text-[#5C5C59]">Mensagem enviada quando este menu é acionado.</p>
          <div className="mt-2 space-y-2">
            <MessageBlock msg={f.message} onChange={(k, v) => setMsg(k, v)} />
            {f.messages.map((m, i) => (
              <div key={m.id || `msg-${i}`} className="pt-2 border-t border-[#E5E5E0]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-[#5C5C59]">Mensagem adicional {i + 1}</span>
                  <button onClick={() => removeExtraMsg(i)} className="text-[#DC2626] text-[10px]"><Trash size={11} weight="bold" /></button>
                </div>
                <MessageBlock msg={m} onChange={(k, v) => setExtraMsg(i, { [k]: v })} />
              </div>
            ))}
          </div>
          <Button onClick={addExtraMsg} size="sm" className="rounded-none mt-2 bg-[#0F4C3A] hover:bg-[#0A3629] text-white w-full" data-testid="add-message-btn">
            <Plus size={12} weight="bold" className="mr-1" /> Adicionar mensagem
          </Button>
        </fieldset>

        {/* Formato */}
        <fieldset className="border border-[#E5E5E0] p-3">
          <legend className="text-xs uppercase font-semibold px-1">Formato</legend>
          <div className="space-y-1 mt-2">
            {[
              { v: "options", l: "Opções pré-definidas" },
              { v: "text", l: "Texto (cliente digita)" },
              { v: "external_request", l: "Requisição externa" },
            ].map((o) => (
              <label key={o.v} className="flex items-center gap-2 text-sm">
                <input type="radio" name="format" value={o.v} checked={f.format === o.v} onChange={() => setField("format", o.v)} data-testid={`format-${o.v}`} />
                {o.l}
              </label>
            ))}
          </div>
          {f.format === "options" && (
            <div className="mt-3 p-2 bg-[#F7F7F5] border border-[#E5E5E0]">
              <p className="text-[10px] uppercase font-semibold tracking-wider mb-1">Formato de menu</p>
              <label className="flex items-center gap-2 text-xs">
                <Switch checked={f.prefer_buttons_or_lists} onCheckedChange={(v) => setField("prefer_buttons_or_lists", v)} data-testid="switch-buttons" />
                Preferir botões ou listas
              </label>
              <p className="text-[10px] text-[#5C5C59] mt-1">
                ⚠️ <b>Só funciona na API Oficial (Meta Cloud API)</b>. Com Evolution/Baileys, WhatsApp entrega como texto numerado normal.
                Se ativar, texto das opções tem <b>máximo {WA_OPTION_LIMIT} caracteres</b>.
              </p>
              {totalOverLimit > 0 && f.prefer_buttons_or_lists && (
                <p className="text-[10px] text-[#DC2626] mt-1 font-semibold">
                  ⚠️ {totalOverLimit} opção(ões) acima do limite
                </p>
              )}
            </div>
          )}
        </fieldset>

        {/* Options branch */}
        {f.format === "options" && (
          <fieldset className="border border-[#E5E5E0] p-3">
            <legend className="text-xs uppercase font-semibold px-1">Opções do menu</legend>
            {f.options.length === 0 && <p className="text-xs text-[#5C5C59] mt-2">Nenhuma opção. Adicione uma abaixo.</p>}
            {f.options.map((op, idx) => {
              const overLimit = f.prefer_buttons_or_lists && (op.text || "").length > WA_OPTION_LIMIT;
              return (
                <div key={op.id || `option-${idx}`} className="mt-3 pt-3 border-t border-[#E5E5E0] first:border-t-0 first:pt-0 first:mt-0" data-testid={`option-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">Opção {idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveOption(idx, -1)} disabled={idx === 0} className="text-[#5C5C59] text-[10px] px-1 disabled:opacity-30">▲</button>
                      <button onClick={() => moveOption(idx, 1)} disabled={idx === f.options.length - 1} className="text-[#5C5C59] text-[10px] px-1 disabled:opacity-30">▼</button>
                      <button onClick={() => removeOption(idx)} className="text-[#DC2626] text-xs" title="Remover">
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  </div>
                  <Label className="text-xs mt-2 flex justify-between">
                    <span>Texto exibido</span>
                    {f.prefer_buttons_or_lists && (
                      <span className={overLimit ? "text-[#DC2626] font-mono" : "text-[#5C5C59] font-mono"}>
                        {(op.text || "").length}/{WA_OPTION_LIMIT}
                      </span>
                    )}
                  </Label>
                  <Input value={op.text} onChange={(e) => setOption(idx, { text: e.target.value })} className={`rounded-none mt-1 ${overLimit ? "border-[#DC2626]" : ""}`} placeholder="Ex.: 1 - Suporte" data-testid={`option-text-${idx}`} />
                  <Label className="text-xs mt-2">Ação</Label>
                  <Select value={op.action} onValueChange={(v) => setOption(idx, { action: v, action_config: {} })}>
                    <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white rounded-none">
                      {ACTIONS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {op.action === "go_to_menu" && (
                    <>
                      <Label className="text-xs mt-2">Menu de destino</Label>
                      <Select value={op.action_config?.next_flow_id || ""} onValueChange={(v) => setOption(idx, { action_config: { next_flow_id: v } })}>
                        <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Escolha um menu" /></SelectTrigger>
                        <SelectContent className="bg-white rounded-none">
                          {flows.filter((x) => x.id !== f.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.identifier}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {op.action === "transfer_department" && (
                    <>
                      <Label className="text-xs mt-2">Departamento</Label>
                      <Select value={op.action_config?.sector_id || ""} onValueChange={(v) => setOption(idx, { action_config: { ...op.action_config, sector_id: v } })}>
                        <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Escolha um departamento" /></SelectTrigger>
                        <SelectContent className="bg-white rounded-none">
                          {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {op.action === "ai_agent" && (
                    <div className="mt-2 p-2 bg-[#FEF3C7] text-[10px] text-[#78350F]">
                      🤖 Ao acionar, o Agente de IA continua a conversa usando as configurações da aba <b>IA</b>.
                    </div>
                  )}
                </div>
              );
            })}
            <Button onClick={addOption} size="sm" data-testid="add-option-btn" className="rounded-none mt-3 bg-[#0F4C3A] hover:bg-[#0A3629] text-white w-full">
              <Plus size={12} weight="bold" className="mr-1" /> Adicionar opção
            </Button>
          </fieldset>
        )}

        {/* Text branch */}
        {f.format === "text" && (
          <fieldset className="border border-[#E5E5E0] p-3">
            <legend className="text-xs uppercase font-semibold px-1">Validar resposta</legend>
            <Label className="text-xs mt-2">Formato esperado</Label>
            <Select value={f.validation} onValueChange={(v) => setField("validation", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="validation-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                {VALIDATIONS.map((v) => <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </fieldset>
        )}

        {/* External request branch */}
        {f.format === "external_request" && (
          <fieldset className="border border-[#E5E5E0] p-3">
            <legend className="text-xs uppercase font-semibold px-1">Requisição externa</legend>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="col-span-1">
                <Label className="text-xs">Método</Label>
                <Select value={f.external_request.method} onValueChange={(v) => setExt("method", v)}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white rounded-none">
                    {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">URL</Label>
                <Input value={f.external_request.url} onChange={(e) => setExt("url", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="https://api.exemplo.com/consulta" data-testid="ext-url" />
              </div>
            </div>
            <Label className="text-xs mt-2 block">Corpo (JSON — opcional)</Label>
            <Textarea value={f.external_request.body || ""} onChange={(e) => setExt("body", e.target.value)} className="rounded-none mt-1 font-mono text-xs" rows={3} placeholder='{"nome":"{{nome}}"}' />
            <Label className="text-xs mt-2 block">Variável do resultado (opcional)</Label>
            <Input value={f.external_request.result_variable || ""} onChange={(e) => setExt("result_variable", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="Ex.: resposta_api" />
          </fieldset>
        )}

        {/* Action after (fallback) */}
        <fieldset className="border border-[#E5E5E0] p-3">
          <legend className="text-xs uppercase font-semibold px-1">
            {f.format === "options" ? "Se nada for escolhido (fallback)" : "Continuar para"}
          </legend>
          <p className="text-[10px] text-[#5C5C59]">
            {f.format === "options"
              ? "Se o cliente digitar algo fora das opções, o que fazer?"
              : "Depois que o cliente responder, pra onde ir?"}
          </p>
          <Select value={f.action_after?.type || "none"} onValueChange={(v) => setField("action_after", { type: v })}>
            <SelectTrigger className="rounded-none mt-2"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white rounded-none">
              {ACTIONS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {f.action_after?.type === "go_to_menu" && (
            <Select value={f.action_after?.next_flow_id || ""} onValueChange={(v) => setField("action_after", { ...f.action_after, next_flow_id: v })}>
              <SelectTrigger className="rounded-none mt-2"><SelectValue placeholder="Menu de destino" /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                {flows.filter((x) => x.id !== f.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.identifier}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {f.action_after?.type === "transfer_department" && (
            <Select value={f.action_after?.sector_id || ""} onValueChange={(v) => setField("action_after", { ...f.action_after, sector_id: v })}>
              <SelectTrigger className="rounded-none mt-2"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </fieldset>

        {/* Custom invalid-input message (only relevant for options menus) */}
        {f.format === "options" && (
          <fieldset className="border border-[#E5E5E0] p-3" data-testid="invalid-msg-field">
            <legend className="text-xs uppercase font-semibold px-1">Mensagem de erro (opcional)</legend>
            <p className="text-[10px] text-[#5C5C59]">
              Quando o cliente digitar algo que não é uma das opções, esta mensagem é enviada e o menu é repetido.
              Deixe em branco para usar a mensagem padrão &quot;Não entendi&quot;.
            </p>
            <Textarea
              value={f.invalid_input_message || ""}
              onChange={(e) => setField("invalid_input_message", e.target.value)}
              placeholder="Ex.: Por favor, digite apenas o número da opção desejada (1, 2 ou 3)."
              data-testid="invalid-input-message"
              className="rounded-none mt-2 text-xs"
              rows={2}
            />
          </fieldset>
        )}
      </div>
    </div>
  );
}

function MessageBlock({ msg, onChange }) {
  return (
    <>
      <Select value={msg.type || "text"} onValueChange={(v) => onChange("type", v)}>
        <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white rounded-none">
          {MSG_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {msg.type === "text" || !msg.type ? (
        <Textarea value={msg.text || ""} onChange={(e) => onChange("text", e.target.value)} className="rounded-none mt-1 font-serif" rows={2} placeholder="Ex.: Olá! Como posso ajudar?" />
      ) : msg.type === "location" ? (
        <Input value={msg.text || ""} onChange={(e) => onChange("text", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="lat,lng ex: -23.55,-46.63" />
      ) : (
        <Input value={msg.media_url || ""} onChange={(e) => onChange("media_url", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="URL do arquivo" />
      )}
    </>
  );
}


// -----------------------------------------------------------------------------
// Simulator panel: chat-style widget that lets the user try the flow locally
// without spending WhatsApp credits.
function SimulatorPanel({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `sim-${Date.now()}`);

  const start = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/chatbot/simulate/reset", { session_id: sessionId, text: "" });
      const { data } = await api.post("/chatbot/simulate/step", { session_id: sessionId, text: "" });
      const bot = (data.messages || []).map((t, k) => ({ id: `bot-${Date.now()}-${k}`, from: "bot", text: t }));
      setMessages(bot);
      if (data.handoff) {
        setMessages((prev) => [...prev, { id: `sys-${Date.now()}`, from: "sys", text: `Fluxo encerrado: ${data.handoff}` }]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [sessionId]);

  useEffect(() => { start(); }, [start]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((prev) => [...prev, { id: `user-${Date.now()}`, from: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/chatbot/simulate/step", { session_id: sessionId, text });
      const bot = (data.messages || []).map((t, k) => ({ id: `bot-${Date.now()}-${k}`, from: "bot", text: t }));
      setMessages((prev) => [...prev, ...bot]);
      if (data.handoff) {
        setMessages((prev) => [...prev, { id: `sys-${Date.now()}`, from: "sys", text: `Fluxo encerrado: ${data.handoff}` }]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="absolute top-4 right-4 w-[360px] max-h-[calc(100vh-160px)] h-[560px] bg-white border-2 border-[#0F4C3A] shadow-brutal z-20 flex flex-col" data-testid="simulator-panel">
      <div className="px-3 py-2 bg-[#0F4C3A] text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Play size={14} weight="fill" />
          <div>
            <div className="font-black text-sm">Testar chatbot</div>
            <div className="text-[10px] opacity-80">Sem custo · não envia WhatsApp</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={start} title="Reiniciar" className="p-1 hover:bg-[#0A3629]" data-testid="simulator-restart">
            <ArrowClockwise size={14} weight="bold" />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-[#0A3629]" title="Fechar">
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 bg-[#F7F7F5] space-y-2" data-testid="simulator-messages">
        {messages.map((m, i) => (
          <div
            key={m.id || `msg-${i}`}
            className={`text-xs whitespace-pre-wrap px-3 py-2 max-w-[85%] ${
              m.from === "user"
                ? "ml-auto bg-[#DCF8C6] border border-[#B7DFA5]"
                : m.from === "sys"
                ? "mx-auto text-center bg-transparent text-[#5C5C59] italic border-t border-b border-[#E5E5E0] w-full"
                : "mr-auto bg-white border border-[#E5E5E0]"
            }`}
          >
            {m.text}
          </div>
        ))}
        {busy && <div className="text-[10px] text-[#5C5C59] italic">bot digitando…</div>}
      </div>
      <div className="p-2 border-t border-[#E5E5E0] flex gap-1">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Digite como se fosse o cliente…"
          className="rounded-none text-xs"
          data-testid="simulator-input"
          disabled={busy}
        />
        <Button onClick={send} disabled={busy || !input.trim()} className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white px-3" data-testid="simulator-send">
          <PaperPlaneTilt size={14} weight="fill" />
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Templates modal: lets the user pick a pre-built chatbot template and
// apply it to their account (optionally wiping existing menus first).
function TemplatesModal({ onClose, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clearExisting, setClearExisting] = useState(true);

  useEffect(() => {
    api.get("/chatbot/templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const apply = async (id) => {
    if (!id) return;
    if (clearExisting && !window.confirm("Isso vai APAGAR todos os menus atuais do bot e substituir pelo template. Continuar?")) return;
    setApplying(true);
    try {
      const { data } = await api.post("/chatbot/templates/apply", { template_id: id, clear_existing: clearExisting });
      toast.success(`Template aplicado: ${data.inserted} menus criados`);
      onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="templates-modal">
      <div className="bg-white border-2 border-[#0F4C3A] max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 bg-[#0F4C3A] text-white flex items-center justify-between">
          <div>
            <div className="font-black text-sm uppercase tracking-wider">✨ Templates prontos</div>
            <div className="text-[10px] opacity-80">Escolha um modelo pra começar rápido — depois é só editar as opções</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#0A3629]" data-testid="templates-close"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-3 bg-[#FEF3E7] border-b border-[#D97706] flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={clearExisting}
            onChange={(e) => setClearExisting(e.target.checked)}
            data-testid="templates-clear"
            className="accent-[#0F4C3A]"
          />
          <span>Apagar menus atuais antes de aplicar (recomendado se ainda não configurou seu bot)</span>
        </div>
        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#F7F7F5]">
          {templates.length === 0 && <div className="col-span-2 text-center text-xs text-[#5C5C59] py-8">Carregando templates…</div>}
          {templates.map((t) => (
            <div
              key={t.id}
              onClick={() => setSelected(t.id)}
              data-testid={`template-${t.id}`}
              className={`bg-white p-3 border-2 cursor-pointer transition-colors ${selected === t.id ? "border-[#0F4C3A] shadow-brutal" : "border-[#E5E5E0] hover:border-[#0F4C3A]"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{t.icon}</span>
                <div className="font-bold text-sm">{t.name}</div>
              </div>
              <p className="text-[10px] text-[#5C5C59]">{t.description}</p>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-[#E5E5E0] flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-none text-xs">Cancelar</Button>
          <Button
            disabled={!selected || applying}
            onClick={() => apply(selected)}
            data-testid="templates-apply"
            className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white text-xs"
          >
            {applying ? "Aplicando…" : "Aplicar template"}
          </Button>
        </div>
      </div>
    </div>
  );
}

